import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useATMStore } from './store/atmStore'
import { useAuthStore, type AuthUser } from './store/authStore'
import { useTheme } from './hooks/useTheme'
import { useWebSocket } from './hooks/useWebSocket'
import { MOCK_FLEET, MOCK_KPIS, MOCK_EVENTS } from './mockData'
import KPIDashboard from './views/KPIDashboard'
import MapView from './views/MapView'
import AlertConsole from './views/AlertConsole'
import FleetGrid from './views/FleetGrid'
import ErrorCatalog from './views/ErrorCatalog'
import StrategyLab from './views/StrategyLab'
import NcrVisionBlueprint from './views/NcrVisionBlueprint'
import ReportsView from './views/ReportsView'
import HistoricoView from './views/HistoricoView'
import RutasCITView from './views/RutasCITView'
import ModelosView from './views/ModelosView'
import AnalyticsView from './views/AnalyticsView'
import LoginPage from './views/LoginPage'
import AdminUsers from './views/AdminUsers'
import type { ATMEvent, ATMAlert, AlertPriority, EventSeverity, FleetKPIs, SimulationRun } from '@atmvision/types'

// ── Constants ────────────────────────────────────────────────────────────────

const BRAND = {
  primary: '#264E72', lightBlue: '#02AFF0', orange: '#F89421',
  dark: '#0f1419', border: 'rgba(255,255,255,0.08)', text: '#e7ecf3',
}

const TABS = [
  { id: 'dashboard',  label: 'Dashboard',              icon: '▦'  },
  { id: 'map',        label: 'Mapa',                   icon: '◎'  },
  { id: 'analytics',  label: 'Analytics',              icon: '📈'  },
  { id: 'alerts',     label: 'Alertas',                icon: '⚠'  },
  { id: 'fleet',      label: 'Flota',                  icon: '≡'  },
  { id: 'errors',     label: 'Catálogo Errores',       icon: '⊟'  },
  { id: 'strategy',   label: 'Strategy Lab',           icon: '⊕'  },
  { id: 'historico',  label: 'Histórico & Predicción', icon: '📊'  },
  { id: 'rutas',      label: 'Rutas CIT',              icon: '🚐'  },
  { id: 'modelos',    label: 'Modelos',                icon: '🏧'  },
  { id: 'reports',    label: 'Reportes',               icon: '📋'  },
  { id: 'ncr',        label: 'NCR Vision',             icon: '◈'  },
]

const SIM_API     = import.meta.env.VITE_SIM_API_URL     || 'http://localhost:8001'
const COLLECT_API = import.meta.env.VITE_COLLECTION_API_URL || 'http://localhost:8002'
export const REPORTS_API = import.meta.env.VITE_REPORTS_API_URL || 'http://localhost:8003'
const WS_EVENTS   = import.meta.env.VITE_WS_EVENTS       || 'ws://localhost:8001/ws/events'
const WS_KPIS     = import.meta.env.VITE_WS_KPIS         || 'ws://localhost:8001/ws/kpis'
const WS_ALERTS   = import.meta.env.VITE_WS_ALERTS       || null   // null = collection-api not running locally

const PRIORITY_TO_SEVERITY: Record<string, string> = {
  P1: 'CRITICAL', P2: 'HIGH', P3: 'WARNING', P4: 'INFO',
}
const SEVERITY_TO_PRIORITY: Partial<Record<EventSeverity, AlertPriority>> = {
  CRITICAL: 'P1', HIGH: 'P2', WARNING: 'P3',
}

const headerIconBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 8, color: '#fff', cursor: 'pointer',
  fontSize: 16, width: 34, height: 34,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background 0.15s', flexShrink: 0,
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeEvent(raw: Record<string, unknown>): ATMEvent {
  return {
    ...raw,
    term_id: (raw.term_id ?? raw.terminal_id ?? '') as string,
    ts: (raw.ts ?? raw.timestamp ?? new Date().toISOString()) as string,
    severity: (PRIORITY_TO_SEVERITY[raw.severity as string] ?? raw.severity ?? 'INFO') as ATMEvent['severity'],
    lat: (raw.lat ?? 0) as number,
    lon: (raw.lon ?? 0) as number,
    simulated: (raw.simulated ?? true) as boolean,
    collection_strategy: (raw.collection_strategy ?? 'hybrid') as ATMEvent['collection_strategy'],
    metadata: (raw.metadata ?? raw.payload ?? {}) as Record<string, unknown>,
  } as ATMEvent
}

// Convert backend ATMAlert (snake_case Pydantic) to frontend ATMAlert shape
function normalizeBackendAlert(raw: Record<string, unknown>): ATMAlert {
  const priority = (raw.priority ?? 'P4') as AlertPriority
  const isActive = raw.is_active as boolean | undefined
  const status = (raw.status as string | undefined) ??
    (isActive === false ? 'RESOLVED' : 'OPEN')
  const ts = (raw.triggered_at ?? new Date().toISOString()) as string
  return {
    alert_id:         raw.alert_id as string,
    created_at:       ts,
    updated_at:       (raw.resolved_at ?? ts) as string,
    term_id:          (raw.terminal_id ?? raw.term_id ?? '') as string,
    model:            (raw.model ?? '') as ATMAlert['model'],
    zone:             (raw.zone ?? 'Distrito Nacional') as ATMAlert['zone'],
    canonical_code:   (raw.canonical_code ?? '') as string,
    description:      (raw.description ?? '') as string,
    severity:         (PRIORITY_TO_SEVERITY[priority] ?? 'INFO') as ATMAlert['severity'],
    priority,
    status:           status as ATMAlert['status'],
    resolved_at:      raw.resolved_at as string | undefined,
    triggering_events: (raw.correlated_event_ids ?? []) as string[],
  }
}

function formatUptime(pct: number) { return `${pct.toFixed(1)}%` }

// ── Root: auth gate ───────────────────────────────────────────────────────────
// Keep this component minimal — only the auth check, no extra hooks.
// This prevents a Rules-of-Hooks violation: Dashboard's hooks only run when
// Dashboard is mounted (i.e. when the user is authenticated).

export default function App() {
  const { isAuthenticated, user: authUser, logout } = useAuthStore()

  if (!isAuthenticated()) return <LoginPage />

  return <Dashboard authUser={authUser!} logout={logout} />
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ authUser, logout }: { authUser: AuthUser; logout: () => void }) {
  const {
    activeTab, setActiveTab, kpis, updateKPIs,
    addEvent, addAlert, updateAlert, alerts,
    setFleet, setAlerts, setWsConnected, wsConnected,
    setSimulationRun, theme, toggleTheme,
  } = useATMStore()

  const C = useTheme()
  const [showAdmin, setShowAdmin] = useState(false)

  const fleetRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const simPollRef      = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Initial seed + live data bootstrap ──────────────────────────────────
  useEffect(() => {
    setFleet(MOCK_FLEET)
    setAlerts([])
    updateKPIs(MOCK_KPIS)
    MOCK_EVENTS.forEach(addEvent)

    const fetchFleet = () =>
      fetch(`${SIM_API}/api/v1/fleet`).then((r) => r.json())
        .then((data) => {
          const fleet = Array.isArray(data) ? data : data?.atms
          if (Array.isArray(fleet) && fleet.length > 0) setFleet(fleet)
        }).catch(() => {})

    const fetchKPIs = () =>
      fetch(`${SIM_API}/api/v1/kpis`).then((r) => r.json())
        .then((data) => { if (data?.total_atms != null) updateKPIs(data) })
        .catch(() => {})

    const fetchSimRun = () =>
      fetch(`${SIM_API}/api/v1/simulation/runs`).then((r) => r.json())
        .then((runs: SimulationRun[]) => {
          if (!Array.isArray(runs)) return
          const running = runs
            .filter((r) => (r.state as string) === 'RUNNING')
            .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0]
          if (running) setSimulationRun(running)
        }).catch(() => {})

    const fetchActiveAlerts = () =>
      fetch(`${COLLECT_API}/api/v1/alerts`).then((r) => r.json())
        .then((data) => {
          const list: Record<string, unknown>[] = Array.isArray(data.alerts) ? data.alerts : []
          const { alerts: current } = useATMStore.getState()
          const existingIds = new Set(current.map((a) => a.alert_id))
          list.forEach((raw) => {
            const a = normalizeBackendAlert(raw)
            if (!existingIds.has(a.alert_id)) addAlert(a)
          })
        }).catch(() => {})

    fetchFleet(); fetchKPIs(); fetchSimRun(); fetchActiveAlerts()
    fleetRefreshRef.current = setInterval(fetchFleet, 30_000)
    simPollRef.current      = setInterval(fetchSimRun, 5_000)

    return () => {
      if (fleetRefreshRef.current) clearInterval(fleetRefreshRef.current)
      if (simPollRef.current)      clearInterval(simPollRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebSocket handlers ───────────────────────────────────────────────────

  const handleEventsMessage = useCallback((data: unknown) => {
    if (!data || typeof data !== 'object') return
    const raw = data as Record<string, unknown>

    if (raw.type === 'fleet_update') {
      useATMStore.getState().updateDeviceStatus(
        raw.terminal_id as string,
        raw.status as import('@atmvision/types').ATMStatus,
      )
      return
    }

    if (!raw.event_id) return

    const event = normalizeEvent(raw)
    addEvent(event)

    const priority = SEVERITY_TO_PRIORITY[event.severity]
    if (!priority) return

    const code = event.canonical_code ?? (raw.raw_code as string) ?? 'ATM-UNKNOWN'

    // CIT lifecycle events (dispatch/arrived/swap/abort) are operational, not faults.
    // They update fleet status and appear in the event stream, but must NOT create
    // open fault alerts that clutter the AlertConsole.
    const CIT_LIFECYCLE_CODES = new Set([
      'ATM-CIT-001', 'ATM-CIT-002', 'ATM-CIT-003', 'ATM-CIT-004',
    ])
    if (CIT_LIFECYCLE_CODES.has(code)) return

    const { alerts: current } = useATMStore.getState()
    const existing = current.find(
      (a) => a.term_id === event.term_id && a.canonical_code === code &&
             (a.status === 'OPEN' || a.status === 'ACKNOWLEDGED'),
    )

    if (existing) {
      if (!existing.triggering_events.includes(event.event_id)) {
        updateAlert(existing.alert_id, {
          updated_at: event.ts,
          triggering_events: [...existing.triggering_events, event.event_id],
        })
      }
    } else {
      const newAlert: ATMAlert = {
        alert_id: `sim-${event.event_id}`,
        created_at: event.ts, updated_at: event.ts,
        term_id: event.term_id, model: event.model, zone: event.zone,
        canonical_code: code, description: event.description,
        severity: event.severity, priority, status: 'OPEN',
        triggering_events: [event.event_id],
      }
      addAlert(newAlert)
    }
  }, [addEvent, addAlert, updateAlert])

  const handleKPIsMessage = useCallback((data: unknown) => {
    const d = data as Record<string, unknown>
    if (d && d.total_atms != null) updateKPIs(d as unknown as FleetKPIs)
  }, [updateKPIs])

  const handleAlertsMessage = useCallback((data: unknown) => {
    if (!data || typeof data !== 'object') return
    const msg = data as Record<string, unknown>

    if (msg.type === 'alert_snapshot') {
      const list = (msg.alerts as Record<string, unknown>[]) ?? []
      const { alerts: current } = useATMStore.getState()
      const existingIds = new Set(current.map((a) => a.alert_id))
      list.forEach((raw) => {
        const a = normalizeBackendAlert(raw)
        if (!existingIds.has(a.alert_id)) addAlert(a)
      })
      return
    }

    if (msg.type === 'alert_triggered') {
      const raw = msg.alert as Record<string, unknown>
      if (!raw) return
      const incoming = normalizeBackendAlert(raw)
      const { alerts: current } = useATMStore.getState()
      const existing = current.find((a) => a.alert_id === incoming.alert_id)
      if (existing) {
        updateAlert(existing.alert_id, { triggering_events: incoming.triggering_events, updated_at: incoming.updated_at })
      } else {
        addAlert(incoming)
      }
      return
    }

    if (msg.type === 'alert_resolved') {
      const alertId = msg.alert_id as string
      updateAlert(alertId, { status: 'RESOLVED', resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    }
  }, [addAlert, updateAlert])

  const eventsWS = useWebSocket(WS_EVENTS, {
    onMessage: handleEventsMessage,
    onOpen:    () => setWsConnected(true),
    onClose:   () => setWsConnected(false),
  })

  useWebSocket(WS_KPIS,   { onMessage: handleKPIsMessage })
  useWebSocket(WS_ALERTS, { onMessage: handleAlertsMessage })

  const openAlerts = alerts.filter((a) => a.status === 'OPEN' || a.status === 'ACKNOWLEDGED').length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.dark, color: C.text, fontFamily: "'Open Sans', system-ui, sans-serif", overflow: 'hidden' }}>

      {/* ── Header ── */}
      <header style={{
        background: `linear-gradient(135deg, ${BRAND.primary} 0%, #1b3a57 100%)`,
        borderBottom: `1px solid ${BRAND.border}`,
        padding: '0 16px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, height: 52 }}>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
            <div style={{ background: BRAND.orange, borderRadius: 6, padding: '4px 8px', fontWeight: 700, fontSize: 13, color: '#4a2a07', letterSpacing: 0.5 }}>
              ATMVISION
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>Banreservas</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>Red 933 Cajeros — NOC</div>
            </div>
          </div>

          {/* Live badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: wsConnected ? '#22c55e' : '#ef4444',
              boxShadow: wsConnected ? '0 0 0 2px rgba(34,197,94,0.3)' : undefined,
              display: 'inline-block',
              animation: wsConnected ? 'pulse 2s infinite' : undefined,
            }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              {wsConnected ? 'LIVE' : eventsWS.reconnectCount > 0 ? `Reconectando (${eventsWS.reconnectCount})` : 'Sin conexión'}
            </span>
          </div>

          {/* KPI bar + controls */}
          <div style={{ display: 'flex', gap: 16, marginLeft: 'auto', alignItems: 'center' }}>
            <KPIChip label="Total ATMs"      value={kpis?.total_atms ?? 933}                              color={BRAND.lightBlue} />
            <KPIChip label="Disponibilidad"  value={kpis ? formatUptime(kpis.availability_pct) : '—'}    color='#22c55e' />
            <KPIChip label="Alertas Activas" value={openAlerts} color={openAlerts > 10 ? '#ef4444' : BRAND.orange} />
            <KPIChip label="MTTR"            value={kpis ? `${kpis.mttr_minutes}m` : '—'}                color={BRAND.orange} />
            <KPIChip label="TX/hora"         value={kpis?.tx_per_hour?.toLocaleString('es-DO') ?? '—'}   color={BRAND.text} />

            {/* Theme toggle */}
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
              style={headerIconBtn}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {/* User pill + admin + logout */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderLeft: '1px solid rgba(255,255,255,0.15)', paddingLeft: 12 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>
                  {authUser.full_name || authUser.username}
                </div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {authUser.role}
                </div>
              </div>
              {authUser.role === 'admin' && (
                <button onClick={() => setShowAdmin(true)} title="Gestión de usuarios"
                  style={headerIconBtn}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}>
                  👥
                </button>
              )}
              <button onClick={logout} title="Cerrar sesión"
                style={{ ...headerIconBtn, fontSize: 13 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.25)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}>
                ⏻
              </button>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <nav style={{ display: 'flex', gap: 2, marginTop: 2 }}>
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              background: activeTab === tab.id ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${BRAND.lightBlue}` : '2px solid transparent',
              color: activeTab === tab.id ? '#fff' : 'rgba(255,255,255,0.65)',
              padding: '8px 14px', fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
            }}>
              <span style={{ fontSize: 11 }}>{tab.icon}</span>
              {tab.label}
              {tab.id === 'alerts' && openAlerts > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 9, fontWeight: 700, padding: '1px 5px', lineHeight: 1.4 }}>
                  {openAlerts}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      {/* ── Tab content ── */}
      <main style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'dashboard'  && <KPIDashboard />}
        {activeTab === 'map'        && <MapView />}
        {activeTab === 'analytics'  && <AnalyticsView />}
        {activeTab === 'alerts'     && <AlertConsole />}
        {activeTab === 'fleet'      && <FleetGrid />}
        {activeTab === 'errors'     && <ErrorCatalog />}
        {activeTab === 'strategy'   && <StrategyLab />}
        {activeTab === 'historico'  && <HistoricoView />}
        {activeTab === 'rutas'      && <RutasCITView />}
        {activeTab === 'modelos'    && <ModelosView />}
        {activeTab === 'reports'    && <ReportsView />}
        {activeTab === 'ncr'        && <NcrVisionBlueprint />}
      </main>

      {showAdmin && <AdminUsers onClose={() => setShowAdmin(false)} />}

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
          50%       { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
        }
      `}</style>
    </div>
  )
}

// ── KPI chip ─────────────────────────────────────────────────────────────────

function KPIChip({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
    </div>
  )
}
